import React from 'react';
import { addDays, format, subDays, subMonths } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import type { DateRange } from 'react-day-picker';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateRangeSelectorProps {
  dateRange: DateRange | undefined;
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
}

export const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({ 
  dateRange, 
  setDateRange 
}) => {
  const applyPreset = (preset: string) => {
    const today = new Date();
    
    switch (preset) {
      case '7d':
        setDateRange({ 
          from: subDays(today, 7), 
          to: today 
        });
        break;
      case '30d':
        setDateRange({ 
          from: subDays(today, 30), 
          to: today 
        });
        break;
      case '90d':
        setDateRange({ 
          from: subDays(today, 90), 
          to: today 
        });
        break;
      case '6m':
        setDateRange({ 
          from: subMonths(today, 6), 
          to: today 
        });
        break;
      case '1y':
        setDateRange({ 
          from: subMonths(today, 12), 
          to: today 
        });
        break;
      default:
        break;
    }
  };
  
  return (
    <div className="flex gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "justify-start text-left font-normal text-hormony-charcoal",
              !dateRange && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-1 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, "LLL d, y")} -{" "}
                  {format(dateRange.to, "LLL d, y")}
                </>
              ) : (
                format(dateRange.from, "LLL d, y")
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
          />
          
          <div className="flex items-center justify-between px-3 py-2 border-t">
            <p className="text-sm text-muted-foreground">
              Quick select:
            </p>
            <div className="space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset('7d')}
              >
                7d
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset('30d')}
              >
                30d
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => applyPreset('90d')}
              >
                90d
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
      
      <Select
        defaultValue="30d"
        onValueChange={applyPreset}
      >
        <SelectTrigger className="w-[110px] h-9">
          <SelectValue placeholder="Time range" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="7d">Last 7 days</SelectItem>
          <SelectItem value="30d">Last 30 days</SelectItem>
          <SelectItem value="90d">Last 90 days</SelectItem>
          <SelectItem value="6m">Last 6 months</SelectItem>
          <SelectItem value="1y">Last year</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
};
